"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.AppHeader = AppHeader;
exports.AppHeaderLeft = AppHeaderLeft;
exports.AppHeaderRight = AppHeaderRight;
exports.AppHeaderBottom = AppHeaderBottom;
exports.AppHeaderBottomLeft = AppHeaderBottomLeft;
exports.AppHeaderBottomMain = AppHeaderBottomMain;
const jsx_runtime_1 = require("react/jsx-runtime");
const client_1 = require("@kalkulacka-one/design-system/client");
const next_intl_1 = require("next-intl");
const react_1 = __importStar(require("react"));
const embeds_1 = require("../embeds/index.js");
const tailwind_1 = require("../../utilities/tailwind.js");
const hasChildOfType = (children, type) => react_1.default.Children.toArray(children).some((child) => react_1.default.isValidElement(child) && child.type === type);
const hasNestedChildOfType = (children, parentType, childType) => react_1.default.Children.toArray(children).some((child) => {
    if (react_1.default.isValidElement(child) && child.type === parentType) {
        const childProps = child.props;
        return hasChildOfType(childProps.children, childType);
    }
    return false;
});
// Tracks whether the page has actually scrolled (a few px of hysteresis so it doesn't flicker at the very top),
// independent of any screen's own condense-on-scroll wiring — several screens (intro, review) use `AppHeader`
// without `WithCondenseOnScroll` at all, so the fade below has to work on its own.
function useScrolled(threshold = 8) {
    const [scrolled, setScrolled] = (0, react_1.useState)(false);
    (0, react_1.useLayoutEffect)(() => {
        let ticking = false;
        const handleScroll = () => {
            if (!ticking) {
                requestAnimationFrame(() => {
                    setScrolled(window.scrollY > threshold);
                    ticking = false;
                });
                ticking = true;
            }
        };
        handleScroll();
        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
    }, [threshold]);
    return scrolled;
}
function AppHeader({ children, condensed = false, calculator, heading }) {
    const t = (0, next_intl_1.useTranslations)("koa");
    const embed = (0, embeds_1.useEmbed)();
    const scrolled = useScrolled();
    const hasPageHeading = hasChildOfType(children, AppHeaderBottom);
    const hasBottomLeft = hasNestedChildOfType(children, AppHeaderBottom, AppHeaderBottomLeft);
    const expand = hasPageHeading && !condensed;
    const gridClasses = "koa:grid koa:grid-cols-[auto_1fr_auto] koa:items-center";
    const expandedRowsClasses = "koa:grid-rows-[3rem_auto]";
    const collapsedRowsClasses = "koa:grid-rows-[3rem]";
    const gridSpacingClasses = "koa:gap-x-2 koa:sm:gap-x-3 koa:gap-y-2 koa:sm:gap-y-3";
    const headerGridClasses = (0, tailwind_1.twMerge)(gridClasses, expand ? expandedRowsClasses : collapsedRowsClasses, gridSpacingClasses);
    const mainGrid = "koa:grid koa:grid-flow-col koa:grid-cols-[auto_1fr] koa:gap-2 koa:min-w-0";
    const mainExpandedOrNoLeftContent = expand || !hasBottomLeft ? "koa:col-span-2" : "";
    const mainCondensedWithLeftContent = condensed && hasBottomLeft ? "koa:col-start-2" : "";
    const mainClasses = (0, tailwind_1.twMerge)(mainGrid, mainExpandedOrNoLeftContent, mainCondensedWithLeftContent);
    const bottomExpanded = "koa:col-span-3 koa:flex koa:items-center koa:h-full";
    const bottomCondensed = "koa:row-start-1";
    const bottomClasses = expand ? bottomExpanded : bottomCondensed;
    // Two layers, its own stacking context via `isolate` so `-z-10` stays behind just this header's own content:
    // `before:` is the header's own box in solid page colour, so the wordmark never collides with content
    // scrolling underneath. At rest nothing sits under the header, and a solid band would show against the page
    // backdrop's glow, so it appears together with the tail once `useScrolled` reports real movement.
    // `after:` is the soft page-colour tail below the box, like 2026's bar — but unlike 2026 it is not needed at
    // rest (nothing scrolls under a header that is already opaque), so it stays invisible until `useScrolled`
    // confirms the page has actually moved, then fades in. That keeps the intro/review screens' content (which
    // never wire up condense-on-scroll) from reading dimmed on first paint.
    const headerBoxClasses = "koa:@container koa:sticky koa:top-0 koa:isolate koa:before:content-[''] koa:before:pointer-events-none koa:before:absolute koa:before:inset-0 koa:before:-z-10 koa:before:bg-page koa:before:transition-opacity koa:before:duration-base koa:after:content-[''] koa:after:pointer-events-none koa:after:absolute koa:after:inset-x-0 koa:after:top-full koa:after:-z-10 koa:after:h-[var(--ko-spacing-fade-edge)] koa:after:bg-[image:var(--ko-fade-to-top)] koa:after:transition-opacity koa:after:duration-base koa:after:ease-out";
    const headerClasses = (0, tailwind_1.twMerge)(headerBoxClasses, scrolled ? "koa:before:opacity-100 koa:after:opacity-100" : "koa:before:opacity-0 koa:after:opacity-0");
    return ((0, jsx_runtime_1.jsx)("header", { className: headerClasses, children: (0, jsx_runtime_1.jsx)("div", { className: "koa:w-full koa:px-gutter koa:py-2 koa:sm:py-3", children: (0, jsx_runtime_1.jsxs)("div", { className: headerGridClasses, children: [(0, jsx_runtime_1.jsx)("div", { className: mainClasses, children: (0, jsx_runtime_1.jsx)(AppHeaderMain, { title: t("appTitle"), heading: heading ?? calculator, logoMonochrome: embed.isEmbed && embed.config?.logo === "monochrome" }) }), react_1.default.Children.map(children, (child) => {
                        if (react_1.default.isValidElement(child) && child.type === AppHeaderRight) {
                            return child.props.children;
                        }
                        return null;
                    }), (expand || (condensed && hasBottomLeft)) && ((0, jsx_runtime_1.jsx)("div", { className: bottomClasses, children: react_1.default.Children.map(children, (child) => {
                            if (react_1.default.isValidElement(child) && child.type !== AppHeaderMain && child.type !== AppHeaderRight) {
                                return react_1.default.cloneElement(child, {
                                    ...(child.props || {}),
                                    condensed,
                                });
                            }
                            return null;
                        }) }))] }) }) }));
}
function AppHeaderLeft({ children }) {
    return (0, jsx_runtime_1.jsx)(jsx_runtime_1.Fragment, { children: children });
}
function AppHeaderMain({ children, title, heading, logoMonochrome }) {
    return ((0, jsx_runtime_1.jsxs)("div", { className: "koa:grid koa:grid-flow-col koa:items-center koa:gap-[0.875rem] koa:min-w-0", children: [(0, jsx_runtime_1.jsx)(client_1.Logo, { title: title, size: "xsmall", monochrome: logoMonochrome }), (0, jsx_runtime_1.jsxs)("div", { className: "koa:grid koa:gap-0.5 koa:leading-none koa:min-w-0", children: [(0, jsx_runtime_1.jsx)("h1", { className: "koa:font-display koa:text-[11px] koa:leading-[1.2] koa:tracking-[-0.03em] koa:font-bold koa:text-text koa:truncate", children: title }), (0, jsx_runtime_1.jsxs)("div", { className: "koa:font-display koa:text-[11px] koa:leading-[1.2] koa:tracking-[-0.03em] koa:text-text-muted koa:truncate", children: [(0, jsx_runtime_1.jsx)("h2", { className: "koa:font-normal koa:inline", children: heading?.title }), heading?.title && heading?.secondaryTitle && (0, jsx_runtime_1.jsx)("span", { className: "koa:font-normal koa:hidden koa:@[24rem]:inline", children: " \u2022 " }), (0, jsx_runtime_1.jsx)("span", { className: "koa:font-normal koa:hidden koa:@[24rem]:inline", children: heading?.secondaryTitle })] }), children] })] }));
}
function AppHeaderRight({ children }) {
    return (0, jsx_runtime_1.jsx)(jsx_runtime_1.Fragment, { children: children });
}
function AppHeaderBottom({ children }) {
    return (0, jsx_runtime_1.jsx)("div", { className: "koa:grid koa:grid-flow-col koa:items-center koa:gap-2", children: children });
}
function AppHeaderBottomLeft({ children, condensed }) {
    if (condensed) {
        return (0, jsx_runtime_1.jsx)("div", { className: "koa:row-start-1 koa:col-start-1 koa:grid koa:grid-flow-col koa:items-center koa:gap-1", children: children });
    }
    return (0, jsx_runtime_1.jsx)(jsx_runtime_1.Fragment, { children: children });
}
function AppHeaderBottomMain({ children, condensed }) {
    if (condensed) {
        return null;
    }
    return (0, jsx_runtime_1.jsx)(jsx_runtime_1.Fragment, { children: children });
}
AppHeader.Left = AppHeaderLeft;
AppHeader.Right = AppHeaderRight;
AppHeader.Bottom = AppHeaderBottom;
AppHeader.BottomLeft = AppHeaderBottomLeft;
AppHeader.BottomMain = AppHeaderBottomMain;
